import React, {
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DynamicTable from "../../../components/tables/dynamic-table1";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import moment from "moment";
import { DataContext } from "../../../components/layouts/main-layout";
import { useHotkeys } from "../../../components/helper-components/use-hotkeys";
import RegionalRestrictionsFilter from "../../../components/tables/table-filter/regional-restrictions-filter";
import {
    getRegionalRestrictionsListAction,
    getRegionalRestrictionsItemListAction,
    getItemsByStoreIdAction
} from "../../../redux/actions/regional-restrictions-action";
import errorToast from "../../../components/helper-components/error-toast";
import { Ban } from "lucide-react";

const RegionalRestrictionsTable = () => {
    const { permissionFn } = useContext(DataContext);
    const [heading, setHeading] = useState([]);
    const dispatch = useDispatch();
    const [restrictions, setRestrictions] = useState([]);
    const [itemRestrictions, setItemRestrictions] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [reload, setReload] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [stats, setStats] = useState({
        total_restrictions: 0,
        active_regions: 0,
        active_stores: 0,
        restricted_products: 0
    });
    const [viewMode, setViewMode] = useState("store"); // "store" or "item"
    const [filterData, setFilterData] = useState({
        sort_by: "",
        sort_order: "",
        selectColumn: "store_code",
        from: "",
        to: "",
        inputValue: "",
        status: "",
    });
    const [searchParams, setSearchParams] = useState({
        searchColumn: "store_code",
        searchValue: "",
        fromDate: "",
        toDate: "",
    });
    const [searchLoading, setSearchLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [apiMessage, setApiMessage] = useState("");

    // Fetch regional restrictions data from API
    const fetchRegionalRestrictions = useCallback(() => {
        setLoading(true);
        const params = {
            pageNumber: currentPage,
            pageSize: pageSize,
            searchColumn: searchParams.searchColumn,
            searchValue: searchParams.searchValue,
            fromDate: searchParams.fromDate,
            toDate: searchParams.toDate,
        };

        dispatch(getRegionalRestrictionsListAction(
            params,
            (response) => {
                if (response.status && response.data) {
                    // Transform API data to match table format
                    const transformedData = response.data.stores.map((store, index) => ({
                        id: index + 1,
                        store_id: store.store_id || "01JH5W3QMVQVWYTCJ1AY173739", // Use store_id from API or fallback
                        store_code: store.store_code,
                        store_name: store.store_name,
                        city: store.store_city,
                        restricted_items: parseInt(store.restricted_item_count) || 0,
                        restricted_transactions: parseInt(store.restricted_transaction_count) || 0,
                        status: store.status,
                        expanded: false,
                        details: [], // Will be populated when expanded
                        detailsLoading: false
                    }));

                    setRestrictions(transformedData);
                    setDataCount(transformedData.length);
                    setApiMessage(""); // Clear API message on successful data

                    // Update stats if available
                    if (response.data.stats) {
                        setStats(response.data.stats);
                    }
                } else if (response.code === 404) {
                    // Handle 404 - No store restrictions found
                    setRestrictions([]);
                    setDataCount(0);
                    setApiMessage(response.message || "No store restrictions found");
                    setStats({
                        total_restrictions: 0,
                        active_regions: 0,
                        active_stores: 0,
                        restricted_products: 0
                    });
                }
                setLoading(false);
                setSearchLoading(false);
                setIsTyping(false);
            },
            (error) => {
                console.error("Error fetching regional restrictions:", error);
                // Always treat as empty state, never set error to true
                setRestrictions([]);
                setDataCount(0);
                setApiMessage(error?.response?.data?.message || "No store restrictions found");
                setStats({
                    total_restrictions: 0,
                    active_regions: 0,
                    active_stores: 0,
                    restricted_products: 0
                });
                // Never show error state
                setLoading(false);
                setSearchLoading(false);
                setIsTyping(false);
            }
        ));
    }, [dispatch, currentPage, pageSize, searchParams, reload]);

    useEffect(() => {
        fetchRegionalRestrictions();
    }, [fetchRegionalRestrictions]);

    // Fetch regional restrictions data grouped by items
    const fetchRegionalRestrictionsItems = useCallback(() => {
        setLoading(true);
        const params = {
            pageNumber: currentPage,
            pageSize: pageSize,
            searchColumn: searchParams.searchColumn,
            searchValue: searchParams.searchValue,
            fromDate: searchParams.fromDate,
            toDate: searchParams.toDate,
        };

        dispatch(getRegionalRestrictionsItemListAction(
            params,
            (response) => {
                if (response.status && response.data) {
                    // Transform API data to item-based format
                    const transformedData = response.data.map((item, index) => ({
                        id: index + 1,
                        item_id: item.item_id,
                        item_code: item.item_code,
                        item_name: item.item_name,
                        category: item.item_category || 'N/A',
                        restricted_stores: 0, // Will be calculated based on actual data
                        restricted_transactions: 1, // Placeholder
                        status: item.is_active ? "Active" : "Inactive",
                        expanded: false,
                        details: [], // Will be populated when expanded
                        detailsLoading: false
                    }));

                    setItemRestrictions(transformedData);
                    setDataCount(transformedData.length);
                    setApiMessage(""); // Clear API message on successful data
                } else if (response.code === 404) {
                    // Handle 404 - No item restrictions found
                    setItemRestrictions([]);
                    setDataCount(0);
                    setApiMessage(response.message || "No item restrictions found");
                }
                setLoading(false);
                setSearchLoading(false);
                setIsTyping(false);
            },
            (error) => {
                console.error("Error fetching item-based restrictions:", error);
                // Always treat as empty state, never set error to true
                setItemRestrictions([]);
                setDataCount(0);
                setApiMessage(error?.response?.data?.message || "No item restrictions found");
                // Never show error state
                setLoading(false);
                setSearchLoading(false);
                setIsTyping(false);
            }
        ));
    }, [dispatch, currentPage, pageSize, searchParams, reload]);


    const navigate = useNavigate();

    const handleViewModeChange = (mode) => {
        setViewMode(mode);

        // Fetch appropriate data based on view mode
        if (mode === "store") {
            fetchRegionalRestrictions();
        } else if (mode === "item") {
            fetchRegionalRestrictionsItems();
        }
    };

    const handleExpandRow = (id) => {
        if (viewMode === "store") {
            handleExpandStoreRow(id);
        } else {
            handleExpandItemRow(id);
        }
    };

    const handleExpandStoreRow = (id) => {
        const currentItem = restrictions.find(item => item.id === id);

        if (!currentItem) return;

        // If currently expanded, just collapse
        if (currentItem.expanded) {
            setRestrictions(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, expanded: false }
                        : item
                )
            );
            return;
        }

        // If not expanded and no details loaded yet, fetch from API
        if (!currentItem.expanded && currentItem.details.length === 0) {
            // Set loading state
            setRestrictions(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, detailsLoading: true, expanded: true }
                        : item
                )
            );

            // Fetch items for this store
            dispatch(getItemsByStoreIdAction(
                currentItem.store_id,
                (response) => {
                    if (response.status && response.data) {
                        // Transform API response to match expected format
                        const itemsDetails = response.data.map((item, index) => ({
                            id: index + 1,
                            item_code: item.item_code,
                            item_name: item.item_name,
                            restricted_transactions: item.restricted_transactions || "None"
                        }));

                        setRestrictions(prev =>
                            prev.map(item =>
                                item.id === id
                                    ? {
                                        ...item,
                                        details: itemsDetails,
                                        detailsLoading: false,
                                        expanded: true
                                    }
                                    : item
                            )
                        );
                    } else {
                        // Handle error case
                        setRestrictions(prev =>
                            prev.map(item =>
                                item.id === id
                                    ? { ...item, detailsLoading: false, expanded: false }
                                    : item
                            )
                        );
                    }
                },
                (error) => {
                    console.error("Error fetching items for store:", error);
                    errorToast("Failed to fetch items for store");
                    setRestrictions(prev =>
                        prev.map(item =>
                            item.id === id
                                ? { ...item, detailsLoading: false, expanded: false }
                                : item
                        )
                    );
                }
            ));
        } else {
            // If details already loaded, just expand
            setRestrictions(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, expanded: true }
                        : item
                )
            );
        }
    };

    const handleExpandItemRow = (id) => {
        const currentItem = itemRestrictions.find(item => item.id === id);

        if (!currentItem) return;

        // If currently expanded, just collapse
        if (currentItem.expanded) {
            setItemRestrictions(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, expanded: false }
                        : item
                )
            );
            return;
        }

        // For item view, show placeholder stores (we'd need a different API for this)
        // For now, let's just toggle with mock data
        setItemRestrictions(prev =>
            prev.map(item =>
                item.id === id
                    ? {
                        ...item,
                        expanded: true,
                        details: [
                            { store_code: "S001", store_name: "Main Store", restricted_transactions: "Sales Invoice" },
                            { store_code: "S002", store_name: "Branch Store", restricted_transactions: "Purchase Order" }
                        ]
                    }
                    : item
            )
        );
    };

    const tableBodyData = viewMode === "store"
        ? restrictions?.map((item) => {
            return {
                id: item?.id,
                store_code: item?.store_code ?? "--",
                store_name: item?.store_name ?? "--",
                city: item?.city ?? "--",
                restricted_items: item?.restricted_items ?? 0,
                restricted_transactions: item?.restricted_transactions ?? 0,
                status: item?.status ?? "--",
                expanded: item?.expanded || false,
                details: item?.details || []
            };
        })
        : itemRestrictions?.map((item) => {
            return {
                id: item?.id,
                item_code: item?.item_code ?? "--",
                item_name: item?.item_name ?? "--",
                category: item?.category ?? "--",
                restricted_stores: item?.restricted_stores ?? 0,
                restricted_transactions: item?.restricted_transactions ?? 0,
                status: item?.status ?? "--",
                expanded: item?.expanded || false,
                details: item?.details || []
            };
        });

    const paginationProps = {
        pageSize: pageSize,
        setPageSize: setPageSize,
        dataCount: dataCount,
        setCurrentPage: setCurrentPage,
        currentPage: currentPage,
    };

    const handleFilterClick = useCallback(() => {
        setShowFilter((prevShowFilter) => !prevShowFilter);
    }, []);

    const handleNewRestriction = () => {
        navigate("/add-regional-restriction");
    };

    const handleView = (id) => {
        navigate(`/view-regional-restriction/${id}`);
    };

    const handleEdit = (id) => {
        // Get the current item data
        const currentData = viewMode === "store" ? restrictions : itemRestrictions;
        const currentItem = currentData.find(item => item.id === id);

        if (!currentItem) {
            errorToast("Item not found for editing");
            return;
        }

        // If in store mode, we need to ensure sub-table data is loaded
        if (viewMode === "store") {
            if (currentItem.details && currentItem.details.length > 0) {
                // Data is already loaded, navigate with state
                navigate(`/edit-regional-restriction/${id}`, {
                    state: {
                        editData: currentItem,
                        viewMode: viewMode
                    }
                });
            } else {
                // Fetch sub-table data first, then navigate
                dispatch(getItemsByStoreIdAction(
                    currentItem.store_id,
                    (response) => {
                        if (response.status && response.data) {
                            const itemsDetails = response.data.map((item, index) => ({
                                id: index + 1,
                                item_code: item.item_code,
                                item_name: item.item_name,
                                restricted_transactions: item.restricted_transactions || "None"
                            }));

                            const updatedItem = {
                                ...currentItem,
                                details: itemsDetails
                            };

                            navigate(`/edit-regional-restriction/${id}`, {
                                state: {
                                    editData: updatedItem,
                                    viewMode: viewMode
                                }
                            });
                        } else {
                            errorToast("Failed to fetch item details for editing");
                        }
                    },
                    (error) => {
                        console.error("Error fetching items for edit:", error);
                        errorToast("Failed to fetch item details for editing");
                    }
                ));
            }
        } else {
            // For item mode, navigate with current data
            navigate(`/edit-regional-restriction/${id}`, {
                state: {
                    editData: currentItem,
                    viewMode: viewMode
                }
            });
        }
    };

    const handleDelete = (id) => {
        // Handle delete logic
        console.log("Delete restriction:", id);
    };

    // Search functionality handlers
    const handleSearchColumnChange = (e) => {
        setSearchParams(prev => ({
            ...prev,
            searchColumn: e.target.value
        }));
    };

    const handleSearchValueChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value); // Update UI immediately
        setIsTyping(true);
    };

    const handleFromDateChange = (e) => {
        setSearchParams(prev => ({
            ...prev,
            fromDate: e.target.value
        }));
    };

    const handleToDateChange = (e) => {
        setSearchParams(prev => ({
            ...prev,
            toDate: e.target.value
        }));
    };

    // Check if there are any active search parameters
    const hasActiveSearch = () => {
        const hasSearchValue = (searchParams.searchValue && searchParams.searchValue.trim() !== '') ||
            (searchInputValue && searchInputValue.trim() !== '');
        const hasDateRange = searchParams.fromDate || searchParams.toDate;
        return hasSearchValue || hasDateRange;
    };

    // Clear search functionality
    const handleClearSearch = () => {
        setSearchInputValue(""); // Clear the input immediately
        setApiMessage(""); // Clear API message
        setSearchParams({
            searchColumn: "store_code",
            searchValue: "",
            fromDate: "",
            toDate: "",
        });
    };

    // Live search handler - triggers search automatically
    const handleLiveSearch = () => {
        setSearchLoading(true);
        setCurrentPage(1); // Reset to first page when searching
        if (viewMode === "store") {
            fetchRegionalRestrictions();
        } else {
            fetchRegionalRestrictionsItems();
        }
    };

    // Debounced search input effect - only triggers API when user stops typing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setIsTyping(false);
            // Update searchParams with the debounced input value
            setSearchParams(prev => ({
                ...prev,
                searchValue: searchInputValue
            }));
        }, 800); // 800ms debounce - wait longer for user to stop typing

        return () => clearTimeout(timeoutId);
    }, [searchInputValue]);

    // Separate effect for API calls - only triggers when searchParams actually change
    useEffect(() => {
        // Only trigger search if there are actual search parameters
        const hasSearchValue = searchParams.searchValue && searchParams.searchValue.trim() !== '';
        const hasDateRange = searchParams.fromDate || searchParams.toDate;

        if (hasSearchValue || hasDateRange) {
            setSearchLoading(true);
            setCurrentPage(1); // Reset to first page when searching
            if (viewMode === "store") {
                fetchRegionalRestrictions();
            } else {
                fetchRegionalRestrictionsItems();
            }
        }
    }, [searchParams, viewMode]);

    // Hotkey for new restriction
    useHotkeys("n", () => handleNewRestriction(), { enableOnFormTags: true });

    // Hotkey for search
    useHotkeys("/", () => {
        const searchInput = document.querySelector('input[placeholder="Search (/)"]');
        if (searchInput) {
            searchInput.focus();
        }
    }, { enableOnFormTags: true });

    // Hotkey for closing filter
    useHotkeys("escape", () => setShowFilter(false));

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <InnerLayout onclkFn={"/inventory"} title="Regional Restrictions">
            <div className="p-5">
                {/* Summary Cards */}
                <div className="px-[24px] pt-[16px] flex space-x-[15px]">
                    <div className="bg-[var(--trans-stats-con-orange-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between">
                        <div>
                            <label className="text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]">
                                Total Restrictions
                            </label>
                            <div className="text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2">
                                {stats.total_restrictions}
                            </div>
                        </div>
                        <div className="rounded-full px-1 py-1 text-[var(--trans-stats-con-orange-fg)] text-nowrap">
                            <Ban size={16} color="var(--trans-stats-con-orange-fg)" />
                        </div>
                    </div>

                    <div className="bg-[var(--trans-stats-con-green-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between">
                        <div>
                            <label className="text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]">
                                Active Regions
                            </label>
                            <div className="text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2">
                                {stats.active_regions}
                            </div>
                        </div>
                        <div className="rounded-full px-1 py-1 text-[var(--trans-stats-con-green-fg)] text-nowrap">
                            <svg className="w-4 h-4 text-[var(--trans-stats-con-green-fg)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>

                    <div className="bg-[var(--trans-stats-con-blue-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between">
                        <div>
                            <label className="text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]">
                                Active Stores
                            </label>
                            <div className="text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2">
                                {stats.active_stores}
                            </div>
                        </div>
                        <div className="rounded-full px-1 py-1 text-[var(--trans-stats-con-blue-fg)] text-nowrap">
                            <svg className="w-4 h-4 text-[var(--trans-stats-con-blue-fg)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                    </div>

                    <div className="bg-[var(--trans-stats-con-red-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between">
                        <div>
                            <label className="text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]">
                                Restricted Products
                            </label>
                            <div className="text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2">
                                {stats.restricted_products}
                            </div>
                        </div>
                        <div className="rounded-full px-1 py-1 text-[var(--trans-stats-con-red-fg)] text-nowrap">
                            <svg className="w-4 h-4 text-[var(--trans-stats-con-red-fg)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Filters and Actions */}
                <div className="bg-white border-[var(--list-table-border-color)] rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)]">Column:</label>
                            <select
                                value={searchParams.searchColumn}
                                onChange={handleSearchColumnChange}
                                className="border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)]"
                            >
                                <option value="store_code">Store Code</option>
                                <option value="store_name">Store Name</option>
                                <option value="city">City</option>
                                <option value="item_code">Item Code</option>
                                <option value="item_name">Item Name</option>
                                <option value="category">Category</option>
                            </select>
                        </div>

                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="search (/)"
                                    value={searchInputValue}
                                    onChange={handleSearchValueChange}
                                    className="w-full border border-[var(--input-border)] rounded-md pl-3 pr-10 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)]"
                                />
                                {searchLoading ? (
                                    <div className="absolute right-3 top-2.5 w-4 h-4">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary-button-bg-color)]"></div>
                                    </div>
                                ) : isTyping ? (
                                    <div className="absolute right-3 top-2.5 w-4 h-4">
                                        <div className="animate-pulse w-2 h-2 bg-orange-500 rounded-full"></div>
                                    </div>
                                ) : (
                                    <svg className="absolute right-3 top-2.5 w-4 h-4 text-[var(--input-place-color)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)]">From Date:</label>
                            <input
                                type="date"
                                value={searchParams.fromDate}
                                onChange={handleFromDateChange}
                                className="border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)]"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)]">To Date:</label>
                            <input
                                type="date"
                                value={searchParams.toDate}
                                onChange={handleToDateChange}
                                className="border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)]"
                            />
                        </div>

                        <button
                            onClick={handleClearSearch}
                            disabled={!hasActiveSearch()}
                            className="bg-[var(--header-button-bg-color)] border border-[var(--header-button-border-color)] text-[var(--header-button-fg-color)] px-4 py-2 rounded-md text-[length:var(--head-button-font-size)] font-[var(--head-button-font-weight)] flex items-center gap-2 hover:bg-[var(--header-button-hover-color)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--header-button-bg-color)]"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Clear
                        </button>

                        {/* <div className="relative">
                            <button
                                onClick={handleFilterClick}
                                className="bg-[var(--header-button-bg-color)] border-[var(--header-button-border-color)] text-[var(--header-button-fg-color)] px-4 py-2 rounded-md text-[length:var(--head-button-font-size)] font-[var(--head-button-font-weight)] flex items-center gap-2 hover:bg-[var(--header-button-hover-color)]"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                                </svg>
                                Filter
                            </button>
                            {showFilter && (
                                <RegionalRestrictionsFilter
                                    filterData={filterData}
                                    setFilterData={setFilterData}
                                    setShowFilter={setShowFilter}
                                />
                            )}
                        </div> */}

                        <div className="flex border border-[var(--primary-button-bg-color)] rounded-md">
                            <button
                                onClick={() => handleViewModeChange("store")}
                                className={`px-4 py-2 text-[length:var(--head-button-font-size)] font-[var(--head-button-font-weight)] rounded-l-md border-r border-[var(--primary-button-bg-color)] ${viewMode === "store"
                                    ? "bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)]"
                                    : "bg-[var(--header-button-bg-color)] text-[var(--primary-button-bg-color)]"
                                    }`}
                            >
                                By Store
                            </button>
                            <button
                                onClick={() => handleViewModeChange("item")}
                                className={`px-4 py-2 text-[length:var(--head-button-font-size)] font-[var(--head-button-font-weight)] rounded-r-md ${viewMode === "item"
                                    ? "bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)]"
                                    : "bg-[var(--header-button-bg-color)] text-[var(--primary-button-bg-color)]"
                                    }`}
                            >
                                By Item
                            </button>
                        </div>

                        <button
                            onClick={handleNewRestriction}
                            className="bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)] px-4 py-2 rounded-md text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)] flex items-center gap-2 hover:bg-[var(--primary-button-hover-color)]"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            New (N)
                        </button>
                    </div>
                </div>

                {/* Data Table */}
                <div className="px-[24px]">
                    <div className="bg-white border border-[var(--list-table-border-color)] rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-[var(--list-table-border-color)]">
                                <thead className="bg-[var(--list-table-heading-bg)]">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider w-12">
                                            <input type="checkbox" className="rounded border-[var(--input-border)]" />
                                        </th>
                                        {viewMode === "store" ? (
                                            <>
                                                <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">
                                                    Store Code
                                                </th>
                                                <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">
                                                    Store Name
                                                </th>
                                                <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">
                                                    City
                                                </th>
                                                <th className="px-4 py-3 text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider text-center">
                                                    Restricted Item
                                                </th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">
                                                    Item Code
                                                </th>
                                                <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">
                                                    Item Name
                                                </th>
                                                <th className="px-4 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">
                                                    Category
                                                </th>
                                                <th className="px-4 py-3 text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider text-center">
                                                    Restricted Stores
                                                </th>
                                            </>
                                        )}
                                        <th className="px-4 py-3 text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider text-center">
                                            Restricted Transactions
                                        </th>
                                        <th className="px-4 py-3 text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider text-center">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider text-center">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-[var(--list-table-row-bg)] divide-y divide-[var(--list-table-border-color)]">
                                    {tableBodyData.length === 0 ? (
                                        <tr>
                                            <td colSpan={viewMode === "store" ? 7 : 7} className="px-4 py-8 text-center">
                                                <div className="flex flex-col items-center justify-center space-y-2">
                                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <p className="text-gray-500 text-lg font-medium">
                                                        {apiMessage || "No store restrictions found"}
                                                    </p>
                                                    <p className="text-gray-400 text-sm">
                                                        {searchParams.searchValue || searchInputValue || searchParams.fromDate || searchParams.toDate
                                                            ? "Try adjusting your search parameters or clear the filters"
                                                            : "Create your first regional restriction to get started"
                                                        }
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : tableBodyData.map((item) => (
                                        <React.Fragment key={item.id}>
                                            <tr className="hover:bg-[var(--list-table-row-hover-bg)]">
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <input type="checkbox" className="rounded border-[var(--input-border)]" />
                                                </td>
                                                {viewMode === "store" ? (
                                                    <>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <button
                                                                onClick={() => handleExpandRow(item.id)}
                                                                className="flex items-center gap-2 text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]"
                                                            >
                                                                <svg
                                                                    className={`w-4 h-4 transition-transform ${item.expanded ? "rotate-90" : ""
                                                                        }`}
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                                {item.store_code}
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                                            {item.store_name}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                                            {item.city}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] text-center">
                                                            {item.restricted_items}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <button
                                                                onClick={() => handleExpandRow(item.id)}
                                                                className="flex items-center gap-2 text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]"
                                                            >
                                                                <svg
                                                                    className={`w-4 h-4 transition-transform ${item.expanded ? "rotate-90" : ""
                                                                        }`}
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                                {item.item_code}
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                                            {item.item_name}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                                            {item.category}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] text-center">
                                                            {item.restricted_stores}
                                                        </td>
                                                    </>
                                                )}
                                                <td className="px-4 py-3 whitespace-nowrap text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] text-center">
                                                    {item.restricted_transactions}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                                    <span
                                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.status === "Active"
                                                            ? "bg-[var(--status-badge-green-bg)] text-[var(--status-badge-green-fg)]"
                                                            : item.status === "Cancelled"
                                                                ? "bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)]"
                                                                : "bg-[var(--status-badge-yellow-bg)] text-[var(--status-badge-yellow-fg)]"
                                                            }`}
                                                    >
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        {/* <button
                                                            onClick={() => handleView(item.id)}
                                                            className="text-[var(--action-button-view-color)] hover:text-[var(--action-button-view-hover-color)] p-1 rounded hover:bg-[var(--action-button-view-bg-hover)]"
                                                            title="View"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </button> */}
                                                        <button
                                                            onClick={() => handleEdit(item.id)}
                                                            className="text-[var(--action-button-edit-color)] hover:text-[var(--action-button-edit-hover-color)] p-1 rounded hover:bg-[var(--action-button-edit-bg-hover)]"
                                                            title="Edit"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.id)}
                                                            className="text-[var(--action-button-delete-color)] hover:text-[var(--action-button-delete-hover-color)] p-1 rounded hover:bg-[var(--action-button-delete-bg-hover)]"
                                                            title="Delete"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expanded Details */}
                                            {item.expanded && (
                                                <tr className="border-b border-b-[var(--list-table-border-color)]">
                                                    <td colSpan={1}></td>
                                                    <td className="p-0 m-0" colSpan="7">
                                                        <div className="border-collapse">
                                                            <table className="w-full border-collapse mt-0">
                                                                <thead className="bg-[var(--list-table-heading-bg)] z-[10]">
                                                                    <tr className="border-l-[var(--list-table-border-color)] border-l h-[50px]">
                                                                        {viewMode === "store" ? (
                                                                            <>
                                                                                <th className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]">
                                                                                    ITEM CODE
                                                                                </th>
                                                                                <th className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]">
                                                                                    ITEM NAME
                                                                                </th>
                                                                                <th className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]">
                                                                                    RESTRICTED TRANSACTIONS
                                                                                </th>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <th className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]">
                                                                                    STORE CODE
                                                                                </th>
                                                                                <th className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]">
                                                                                    STORE NAME
                                                                                </th>
                                                                                <th className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]">
                                                                                    RESTRICTED TRANSACTIONS
                                                                                </th>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.detailsLoading ? (
                                                                        <tr className="border-l-[var(--list-table-border-color)] border-l">
                                                                            <td colSpan="3" className="py-[20px] px-[10px] h-[50px] text-center">
                                                                                <div className="flex items-center justify-center space-x-2">
                                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary-button-bg-color)]"></div>
                                                                                    <span className="text-[length:--list-table-row-font-size] text-[var(--description-color)]">
                                                                                        {viewMode === "store" ? "Loading items..." : "Loading stores..."}
                                                                                    </span>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ) : item.details && item.details.length > 0 ? (
                                                                        item.details.map((detail, detailIndex) => (
                                                                            <tr
                                                                                key={detailIndex}
                                                                                className="border-l-[var(--list-table-border-color)] border-l"
                                                                            >
                                                                                {viewMode === "store" ? (
                                                                                    <>
                                                                                        <td className={`${detailIndex === item.details.length - 1 ? "" : "border-b border-b-[var(--list-table-border-color)]"} py-[15px] px-[10px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap`}>
                                                                                            {detail.item_code}
                                                                                        </td>
                                                                                        <td className={`${detailIndex === item.details.length - 1 ? "" : "border-b border-b-[var(--list-table-border-color)]"} py-[15px] px-[10px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap`}>
                                                                                            {detail.item_name}
                                                                                        </td>
                                                                                        <td className={`${detailIndex === item.details.length - 1 ? "" : "border-b border-b-[var(--list-table-border-color)]"} py-[15px] px-[10px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap`}>
                                                                                            {detail.restricted_transactions}
                                                                                        </td>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <td className={`${detailIndex === item.details.length - 1 ? "" : "border-b border-b-[var(--list-table-border-color)]"} py-[15px] px-[10px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap`}>
                                                                                            {detail.store_code}
                                                                                        </td>
                                                                                        <td className={`${detailIndex === item.details.length - 1 ? "" : "border-b border-b-[var(--list-table-border-color)]"} py-[15px] px-[10px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap`}>
                                                                                            {detail.store_name}
                                                                                        </td>
                                                                                        <td className={`${detailIndex === item.details.length - 1 ? "" : "border-b border-b-[var(--list-table-border-color)]"} py-[15px] px-[10px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap`}>
                                                                                            {detail.restricted_transactions}
                                                                                        </td>
                                                                                    </>
                                                                                )}
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr className="border-l-[var(--list-table-border-color)] border-l">
                                                                            <td colSpan="3" className="py-[20px] px-[10px] h-[50px] text-center text-[length:--list-table-row-font-size] text-[var(--description-color)]">
                                                                                {viewMode === "store" ? "No restricted items found for this store" : "No restricted stores found for this item"}
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {dataCount > 0 && (
                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-[var(--list-table-border-color)] sm:px-6">
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button className="relative inline-flex items-center px-4 py-2 border-[var(--list-pagination-select-border)] text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] rounded-md text-[var(--list-pagination-text)] bg-[var(--list-pagination-select-bg)] hover:bg-[var(--list-pagination-button-hover-bg)]">
                                        Previous
                                    </button>
                                    <button className="ml-3 relative inline-flex items-center px-4 py-2 border-[var(--list-pagination-select-border)] text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] rounded-md text-[var(--list-pagination-text)] bg-[var(--list-pagination-select-bg)] hover:bg-[var(--list-pagination-button-hover-bg)]">
                                        Next
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] text-[var(--list-pagination-text)]">
                                            {dataCount > 0 ? (
                                                <>
                                                    Showing <span className="font-medium">{Math.min(pageSize, dataCount)}</span> of{" "}
                                                    <span className="font-medium">{dataCount}</span> {viewMode === "store" ? "Stores" : "Items"}
                                                    {(searchParams.searchValue || searchInputValue) && (
                                                        <span className="ml-2 text-blue-600">
                                                            (filtered by "{searchParams.searchValue || searchInputValue}")
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    No {viewMode === "store" ? "stores" : "items"} found
                                                    {(searchParams.searchValue || searchInputValue) && (
                                                        <span className="ml-2 text-blue-600">
                                                            for "{searchParams.searchValue || searchInputValue}"
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                            {searchLoading && (
                                                <span className="ml-2 text-orange-600 flex items-center gap-1">
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                                                    Searching...
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                            <div className="flex items-center gap-2 mr-4">
                                                <select
                                                    value={pageSize}
                                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                                    className="border-[var(--list-pagination-select-border)] bg-[var(--list-pagination-select-bg)] text-[var(--list-pagination-select-fg)] rounded-md px-3 py-1 text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)]"
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                                <span className="text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] text-[var(--list-pagination-text)]">rows / page</span>
                                            </div>
                                            <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border-[var(--list-pagination-select-border)] bg-[var(--list-pagination-button-bg)] text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] text-[var(--list-pagination-text)] hover:bg-[var(--list-pagination-button-hover-bg)]">
                                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border-[var(--list-pagination-select-border)] bg-[var(--list-pagination-button-bg)] text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] text-[var(--list-pagination-text)] hover:bg-[var(--list-pagination-button-hover-bg)]">
                                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </InnerLayout>
    );
};

export default RegionalRestrictionsTable;
